import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Receipt, CheckCircle2, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function ReceiptDialog() {
  const location = useLocation();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const id = new URLSearchParams(location.search).get("receipt");

  useEffect(() => {
    if (!id) { setReceipt(null); return; }
    axios.get(`${API}/wallet/receipts/${id}`).then(r => setReceipt(r.data)).catch(() => setReceipt({ error: true }));
  }, [id]);

  const close = () => navigate("/wallet", { replace: true });
  const isDeposit = receipt?.kind === "deposit";
  const rows = receipt && !receipt.error ? [
    ["Tipo", isDeposit ? "Depósito na carteira" : "Saque da carteira"],
    ["Método", receipt.method],
    ["Valor", brl(receipt.amount)],
    ...(receipt.fee ? [["Taxa", `- ${brl(receipt.fee)}`]] : []),
    [isDeposit ? "Creditado" : "Valor pago", brl(receipt.net)],
    ...(receipt.pix_key ? [["Chave Pix (plataforma)", receipt.pix_key]] : []),
    ...(receipt.destination ? [["Destino", receipt.destination]] : []),
    ["Data", new Date(receipt.confirmed_at).toLocaleString("pt-BR")],
    ["Nº do recibo", receipt.receipt_id],
  ] : [];

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-md" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-primary" /> Recibo</DialogTitle></DialogHeader>
        {receipt?.error && <div className="text-sm text-muted-foreground" data-testid="receipt-error">Recibo não encontrado.</div>}
        {receipt && !receipt.error && (
          <div className="space-y-4" id="receipt-print">
            <div className="rounded-2xl bg-emerald-500/10 p-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <div className="text-2xl font-bold mt-1" data-testid="receipt-amount">{brl(receipt.net)}</div>
              <div className="text-xs text-muted-foreground">{isDeposit ? "Depósito confirmado e creditado" : "Saque pago com sucesso"}</div>
            </div>
            <div className="divide-y text-sm">
              {rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right break-all">{v}</span></div>
              ))}
            </div>
            {receipt.receipt_url && <a href={receipt.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline" data-testid="receipt-proof-link">Ver comprovante enviado</a>}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1 gap-1" onClick={() => window.print()} data-testid="receipt-print"><Printer className="w-4 h-4" /> Imprimir / PDF</Button>
              <Button className="rounded-full flex-1" onClick={close} data-testid="receipt-close">Fechar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
